import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
  Avatar,
  IconButton,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useRef } from "react";
import { Dog, DogDetails } from "../component";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import Cookies from "universal-cookie";
import axios from "axios";
import { PhotoCamera, Pets } from "@mui/icons-material";

export const profilePicturePresign = async (
  file_name: string,
  dog_id: number
) => {
  const response = axios.post(
    `https://api.easyagility.co.uk/dogs/picture/presign?file_name=${file_name}&dog_id=${dog_id}`,
    {},
    {
      headers: {
        Authorization: "Bearer " + new Cookies().get("token"),
      },
    }
  );

  return (await response).data;
};

const EditModal = ({
  open,
  setOpen,
  dog,
  getDogs,
  setSuccessOpen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  dog: Dog;
  getDogs: () => void;
  setSuccessOpen: (open: boolean) => void;
}): JSX.Element => {
  const [details, setDetails] = React.useState<DogDetails>({} as DogDetails);
  const [picturePreview, setPicturePreview] = React.useState<string | null>(
    null
  );
  const [uploadingPicture, setUploadingPicture] = React.useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    // Check if this is a new dog (no id) or an existing dog
    if (!dog.details || dog.details.length === 0 || !dog.details[0]?.id) {
      // This is a new dog - reset form to empty values
      setDetails({
        name: "",
        grade: 1,
        height: "300",
        kc_name: "",
        kc_number: "",
        date_of_birth: "",
        picture_url: "",
        account_id: 0,
      } as DogDetails);
      setPicturePreview(null);
    } else {
      // This is an existing dog - populate form with current values
      const d = dog.details.find(
        (d) => d.grade === Math.max(...dog.details.map((obj) => obj.grade))
      );
      if (d) {
        if (!d.kc_name) {
          d.kc_name = "";
        }
        if (!d.kc_number) {
          d.kc_number = "";
        }
        setDetails(d);
        setPicturePreview(d.picture_url || null);
      }
    }
  }, [dog]);

  const handleSave = async () => {
    const token = new Cookies().get("token");

    try {
      await axios.post("https://api.easyagility.co.uk/dogs", details, {
        headers: { Authorization: "Bearer " + token },
      });
      getDogs();

      setOpen(false);
      setSuccessOpen(true);
    } catch (err) {
      console.log(err);
    }
  };

  const getFileByteStream = (file: File): Promise<any> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handlePictureUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    details: DogDetails
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    try {
      setUploadingPicture(true);

      const presignResponse = await profilePicturePresign(
        file.name,
        details.id || 0
      );

      await axios({
        method: "put",
        url: presignResponse.presigned_url,
        data: await getFileByteStream(file),
        headers: {
          "Content-Type": file.type,
        },
      });

      setDetails({
        ...details,
        picture_url: presignResponse.document_url,
      });

      setPicturePreview(presignResponse.document_url);
    } catch (err) {
      console.error("Error uploading picture:", err);
      alert("Failed to upload picture. Please try again.");
    } finally {
      setUploadingPicture(false);
    }
  };

  const handlePictureClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: isMobile ? "100%" : "90%",
          maxWidth: 600,
          maxHeight: "90vh",
          bgcolor: "background.paper",
          borderRadius: 3,
          boxShadow: 24,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.primary.main,
            color: "white",
          }}
        >
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Edit Dog's Details
          </Typography>
        </Box>

        {/* Scrollable Content */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: 3,
          }}
        >
          {/* Picture Upload Section */}
          <Box sx={{ mb: 3, textAlign: "center" }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
              Dog Picture
            </Typography>
            <Box
              sx={{
                position: "relative",
                display: "inline-block",
                cursor: "pointer",
              }}
              onClick={handlePictureClick}
            >
              {picturePreview ? (
                <Avatar
                  src={picturePreview}
                  sx={{
                    width: 120,
                    height: 120,
                    border: "3px solid",
                    borderColor: "primary.main",
                  }}
                />
              ) : (
                <Avatar
                  sx={{
                    width: 120,
                    height: 120,
                    backgroundColor: "primary.main",
                    border: "3px solid",
                    borderColor: "primary.main",
                  }}
                >
                  <Pets sx={{ fontSize: 60 }} />
                </Avatar>
              )}
              <IconButton
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  backgroundColor: "primary.main",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "primary.dark",
                  },
                }}
                disabled={uploadingPicture}
              >
                <PhotoCamera />
              </IconButton>
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handlePictureUpload(e, details)}
              style={{ display: "none" }}
            />
            {uploadingPicture && (
              <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                Uploading picture...
              </Typography>
            )}
          </Box>

          {/* Form Fields */}
          <Stack spacing={2}>
            <TextField
              value={details.name}
              fullWidth
              onChange={(e) => {
                setDetails({
                  ...details,
                  name: e.target.value,
                });
              }}
              disabled={details.id !== undefined}
              label={"Dog's Name"}
              variant="outlined"
            />

            <FormControl fullWidth>
              <InputLabel id="grade-label">Grade</InputLabel>
              <Select
                onChange={(e) => {
                  setDetails({
                    ...details,
                    grade: parseInt(e.target.value as string),
                  });
                }}
                labelId="grade-label"
                value={details?.grade || 1}
                label={"Grade"}
              >
                <MenuItem disabled={details.grade > 1} value={1}>
                  1
                </MenuItem>
                <MenuItem disabled={details.grade > 2} value={2}>
                  2
                </MenuItem>
                <MenuItem disabled={details.grade > 3} value={3}>
                  3
                </MenuItem>
                <MenuItem disabled={details.grade > 4} value={4}>
                  4
                </MenuItem>
                <MenuItem disabled={details.grade > 5} value={5}>
                  5
                </MenuItem>
                <MenuItem disabled={details.grade > 6} value={6}>
                  6
                </MenuItem>
                <MenuItem value={7}>7</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="height-label">Height</InputLabel>
              <Select
                onChange={(e) => {
                  setDetails({
                    ...details,
                    height: e.target.value as string,
                  });
                }}
                labelId="height-label"
                value={details?.height || "300"}
                label={"Height"}
              >
                <MenuItem value={"200/300"}>200/300</MenuItem>
                <MenuItem value={"300"}>300</MenuItem>
                <MenuItem value={"400"}>400</MenuItem>
                <MenuItem value={"500"}>500</MenuItem>
                <MenuItem value={"600"}>600</MenuItem>
              </Select>
            </FormControl>

            <TextField
              onChange={(e) => {
                setDetails({
                  ...details,
                  kc_name: e.target.value,
                });
              }}
              value={details?.kc_name}
              fullWidth
              label={"KC Name"}
              variant="outlined"
            />

            <TextField
              onChange={(e) => {
                setDetails({
                  ...details,
                  kc_number: e.target.value,
                });
              }}
              value={details?.kc_number}
              fullWidth
              label={"KC Number"}
              variant="outlined"
            />

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Date of Birth"
                value={
                  details?.date_of_birth ? dayjs(details?.date_of_birth) : null
                }
                format="DD/MM/YYYY"
                onChange={(value: Dayjs | null): void => {
                  setDetails({
                    ...details,
                    date_of_birth: value?.format("YYYY-MM-DD") || "",
                  });
                }}
                slotProps={{
                  textField: {
                    required: true,
                    fullWidth: true,
                    variant: "outlined",
                  },
                }}
              />
            </LocalizationProvider>
          </Stack>
        </Box>

        {/* Footer with Buttons */}
        <Box
          sx={{
            p: 3,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.grey[50],
            display: "flex",
            gap: 2,
            justifyContent: "flex-end",
          }}
        >
          <Button
            onClick={() => {
              setOpen(false);
            }}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!details.name || !details.date_of_birth}
            sx={{ textTransform: "none" }}
          >
            Save changes
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditModal;
